import { TranslocoService } from '@jsverse/transloco';
import { decompressRTF } from '@kenjiuno/decompressrtf';
import MsgReader from '@kenjiuno/msgreader';
import DOMPurify from 'dompurify';
import { DocumentAttachment } from 'models';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import PostalMime from 'postal-mime';
import {
  convertRtfToHtml,
  replaceCidReferencesWithDataUrls,
} from './rtf-to-html';

/** Describes an email address. */
export interface EmailAddressInterface {
  /** Essentially the display name. */
  name?: string;
  /** Email address. */
  email?: string;
}

/** Describes options for converting an email to HTML. */
export interface EmailToHtmlOptions {
  /**
   * Determines if an image load warning should be displayed.
   * This should be a translated warning message if provided.
   */
  imageLoadWarning?: string;
}

/** Describes email priority. */
export type EmailPriority = 'high' | 'normal' | 'low';

/** Class used for representing email data. */
export class EmailData {
  /** CC. */
  cc?: EmailAddress[];
  /** Sender. */
  sender?: EmailAddress;
  /** Sent date. */
  date?: string;
  /** Recipients. */
  recipients?: EmailAddress[];
  /** Subject. */
  subject?: string;
  /** Body. */
  body?: string;
  /** Priority level when available in the source message headers. */
  priority?: EmailPriority;
  /* Attachments. This does not include attachments that are in the message body. */
  attachments: DocumentAttachment[];
  /**
   * Whether the email contains images with external resources.
   *
   * This seems to only apply for MSG files.
   */
  containsImageTagsWithExternalSources: boolean;

  constructor(
    sender?: EmailAddressInterface,
    date?: string,
    recipients?: EmailAddressInterface[],
    subject?: string,
    body?: string,
    attachments?: DocumentAttachment[],
    cc?: EmailAddressInterface[],
    containsImageTagsWithExternalSources = false,
    priority?: EmailPriority
  ) {
    this.sender = new EmailAddress(sender);
    this.date = date;
    this.recipients = recipients?.map((r) => new EmailAddress(r));
    this.subject = subject;
    this.body = body;
    this.priority = priority;
    this.attachments = attachments ?? [];
    this.cc = cc?.map((x) => new EmailAddress(x));
    this.containsImageTagsWithExternalSources =
      containsImageTagsWithExternalSources;
  }

  static async fromEml(emlArrayBuffer: ArrayBuffer): Promise<EmailData> {
    const email = await PostalMime.parse(emlArrayBuffer);
    const attachments = email.attachments.map((a) => ({
      name: a.filename ?? 'file',
      bytes: a.content as ArrayBuffer,
    }));

    let body = email.text;
    if (email.html) {
      // Get the HTML body
      let html = email.html;
      // Replace CID references with data URLs for related attachments
      for (const attachment of email.attachments.filter((a) => a.related)) {
        if (!attachment.contentId || typeof attachment.content === 'string')
          continue;

        const contentId = attachment.contentId.replace(/^<|>$/g, '');
        const dataUrl = arrayBufferToDataUrl(
          attachment.content,
          attachment.mimeType
        );
        html = html.replace(new RegExp(`cid:${contentId}`, 'gi'), dataUrl);
      }
      // Return sanitized HTML, after all CID references have been replaced
      body = DOMPurify.sanitize(html);
    }

    const priority = getPriorityFromHeaderList(email.headers);

    const emailData = new EmailData(
      {
        name: email.from?.name
          ? DOMPurify.sanitize(email.from.name)
          : undefined,
        email: email.from?.address,
      },
      email.date ? DOMPurify.sanitize(email.date) : undefined,
      email.to?.map((r) => ({
        name: r.name ? DOMPurify.sanitize(r.name) : undefined,
        email: r.address,
      })),
      email.subject ? DOMPurify.sanitize(email.subject) : undefined,
      body,
      attachments,
      email.cc?.map((r) => ({
        name: r.name ? DOMPurify.sanitize(r.name) : undefined,
        email: r.address,
      })),
      false,
      priority
    );

    return emailData;
  }

  static async fromMsg(
    msgArrayBuffer: ArrayBuffer,
    translateService: TranslocoService,
    logger: NGXLogger
  ): Promise<EmailData> {
    const msgReader = new MsgReader(msgArrayBuffer);
    const email = msgReader.getFileData();
    const attachments: DocumentAttachment[] =
      email.attachments?.map((a) => {
        const attachmentData = msgReader.getAttachment(a);
        return {
          name: attachmentData.fileName,
          bytes: attachmentData.content.buffer,
        };
      }) ?? [];

    let body = '';
    let hasExternalImages = false;
    if (email.html) {
      body = new TextDecoder().decode(email.html);
      const imgRe = /<img\b[^>]+\bsrc=(["'])(https?:\/\/[^"']+)\1/i;
      hasExternalImages = imgRe.test(body);
    } else if (email.compressedRtf) {
      try {
        const decompressedRtfBytes = decompressRTF(
          Array.from(email.compressedRtf)
        );
        const rtfString = new TextDecoder().decode(
          new Uint8Array(decompressedRtfBytes)
        );
        // Log the full RTF string for debugging, as the parsing is very basic and may fail on certain inputs.
        logger.debug('Decompressed RTF string from MSG body:', rtfString);
        // Very basic RTF to HTML conversion.
        body = convertRtfToHtml(rtfString);
        // Note: We don't call textToHtml() here because we've already extracted HTML from the RTF
      } catch (error) {
        logger.warn('Failed to parse RTF body from MSG.', error);
      }
    } else if (email.body) {
      body = email.body;
    }

    // Replace cid: references in img tags with data URLs from attachments
    body = replaceCidReferencesWithDataUrls(body, attachments);

    // Sanitize body for MSG files
    body = DOMPurify.sanitize(body);

    // If we couldn't obtain a body, show a message indicating it is unavailable instead of leaving it blank.
    if (!body) {
      body = translateService.translate('MSG_BODY_UNAVAILABLE');
    }

    const priority = getPriorityFromRawHeaders(email.headers);

    const emailData = new EmailData(
      {
        name: email.senderName
          ? DOMPurify.sanitize(email.senderName)
          : undefined,
        email: email.senderEmail,
      },
      email.messageDeliveryTime
        ? DOMPurify.sanitize(email.messageDeliveryTime)
        : undefined,
      email.recipients?.map((r) => ({
        name: r.name ? DOMPurify.sanitize(r.name) : undefined,
        email: r.email,
      })),
      email.subject ? DOMPurify.sanitize(email.subject) : undefined,
      body,
      attachments,
      email.recipients
        ?.filter((r) => r.recipType === 'cc')
        .map((r) => ({
          name: r.name ? DOMPurify.sanitize(r.name) : undefined,
          email: r.email,
        })),
      hasExternalImages,
      priority
    );

    return emailData;
  }

  /**
   * Provides the email data as an HTML element.
   *
   * @returns The HTMLDivElement representing the email.
   */
  toHtml(options?: EmailToHtmlOptions): HTMLDivElement {
    const emailElement = document.createElement('div');
    Object.assign(emailElement.style, {
      position: 'absolute',
      display: 'inline-block',
      left: '-9999px',
      width: '8.5in',
      'min-height': '11in',
      padding: '25px',
    });

    const senderString = this.sender?.toHtmlString() ?? undefined;

    if (senderString) {
      const senderElement = document.createElement('div');
      senderElement.innerHTML = '<strong>From:</strong>&nbsp;';
      const senderSpan = document.createElement('span');
      senderSpan.textContent = senderString;
      senderElement.appendChild(senderSpan);
      emailElement.appendChild(senderElement);
    }

    if (this.date) {
      let deliveryDate: string;
      try {
        deliveryDate = moment(this.date).format('LLLL');
      } catch (error) {
        // We can't parse the date so just insert whatever string was provided.
        deliveryDate = this.date;
      }

      const sentDateElement = document.createElement('div');
      sentDateElement.innerHTML = '<strong>Sent:</strong>&nbsp;';
      const dateSpan = document.createElement('span');
      dateSpan.textContent = deliveryDate;
      sentDateElement.appendChild(dateSpan);
      emailElement.appendChild(sentDateElement);
    }

    if (this.recipients) {
      const recipients = this.recipients
        .map((r) => r.toHtmlString())
        .filter((r) => !!r) as string[]; // Filter out undefined recipients and cast to string[] since we know undefined entries are gone

      if (recipients.length > 0) {
        const recipientElement = document.createElement('div');
        recipientElement.innerHTML = '<strong>To:</strong>&nbsp;';
        const recipientSpan = document.createElement('span');
        recipientSpan.textContent = recipients.join(', ');
        recipientElement.appendChild(recipientSpan);
        emailElement.appendChild(recipientElement);
      }
    }

    const subjectElement = document.createElement('div');
    subjectElement.innerHTML = '<strong>Subject:</strong>&nbsp;';
    const subjectSpan = document.createElement('span');
    subjectSpan.textContent = this.subject ?? '';
    subjectElement.appendChild(subjectSpan);
    emailElement.appendChild(subjectElement);

    const bodyElement = document.createElement('div');
    bodyElement.setAttribute('style', 'padding-top: 25px');
    bodyElement.innerHTML = this.body ?? '';
    if (this.containsImageTagsWithExternalSources) {
      const warningElement = document.createElement('div');
      warningElement.setAttribute(
        'style',
        'background:#fff9c4;border:1px solid #ccc;padding:12px;border-radius:4px;margin-top:12px;'
      );
      warningElement.innerHTML =
        options?.imageLoadWarning ?? 'Unable to load linked images.';
      bodyElement.appendChild(warningElement);
    }
    emailElement.appendChild(bodyElement);

    return emailElement;
  }
}

/** Email address class. */
export class EmailAddress implements EmailAddressInterface {
  name?: string;
  email?: string;

  constructor(emailAddress?: Partial<EmailAddressInterface>) {
    this.name = emailAddress?.name;
    this.email = emailAddress?.email;
  }

  get isEmpty(): boolean {
    return !this.name && !this.email;
  }

  /**
   * Converts the email address to a string.
   *
   * @returns A string to be used for display or undefined if there is no data for the address.
   */
  toString(): string | undefined {
    if (this.isEmpty) {
      return undefined;
    }

    if (this.name && this.email) {
      return `${this.name} <${this.email}>`;
    }

    // Getting here means at least one of the fields is populated
    return this.name || this.email;
  }

  /**
   * Converts the email address to a string for display.
   * The string is safe for use with textContent (no HTML escaping needed).
   *
   * @returns A string to be used for display or undefined if there is no data for the address.
   */
  toHtmlString(): string | undefined {
    if (this.isEmpty) {
      return undefined;
    }

    if (this.name && this.email) {
      return `${this.name} <${this.email}>`;
    }

    // Getting here means at least one of the fields is populated
    return this.name || this.email;
  }
}

/**
 * Converts an ArrayBuffer or Uint8Array to a data URL.
 *
 * @param buffer Array buffer or typed array.
 * @param mimeType
 * @returns A data URL.
 */
export const arrayBufferToDataUrl = (
  buffer: ArrayBuffer | Uint8Array,
  mimeType: string
) => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return `data:${mimeType};base64,${btoa(binary)}`;
};

const resolvePriority = (
  xPriority?: string,
  importance?: string,
  priority?: string
): EmailPriority | undefined => {
  const normalizedXPriority = xPriority?.toLowerCase().trim();
  const xPriorityMatch = normalizedXPriority?.match(/[1-5]/)?.[0];

  if (xPriorityMatch) {
    const xPriorityValue = Number(xPriorityMatch);
    if (xPriorityValue <= 2) {
      return 'high';
    }
    if (xPriorityValue >= 4) {
      return 'low';
    }
    return 'normal';
  }

  const normalizedImportance = importance?.toLowerCase().trim();
  if (normalizedImportance === 'high') {
    return 'high';
  }
  if (normalizedImportance === 'normal') {
    return 'normal';
  }
  if (normalizedImportance === 'low') {
    return 'low';
  }

  const normalizedPriority = priority?.toLowerCase().trim();
  if (normalizedPriority === 'urgent') {
    return 'high';
  }
  if (normalizedPriority === 'normal') {
    return 'normal';
  }
  if (normalizedPriority === 'non-urgent') {
    return 'low';
  }

  return undefined;
};

const getPriorityFromHeaderList = (
  headers: { key: string; value: string }[]
): EmailPriority | undefined => {
  const getHeader = (...keys: string[]) =>
    headers.find((header) => keys.includes(header.key))?.value;

  return resolvePriority(
    getHeader('x-priority', 'x-msmail-priority'),
    getHeader('importance'),
    getHeader('priority')
  );
};

const getPriorityFromRawHeaders = (
  rawHeaders?: string
): EmailPriority | undefined => {
  if (!rawHeaders) {
    return undefined;
  }

  const unfoldedHeaders = rawHeaders.replace(/\r?\n[ \t]+/g, ' ');
  const getHeader = (key: string) =>
    unfoldedHeaders.match(new RegExp(`^${key}\\s*:\\s*(.+)$`, 'im'))?.[1];

  return resolvePriority(
    getHeader('x-priority') ?? getHeader('x-msmail-priority'),
    getHeader('importance'),
    getHeader('priority')
  );
};
